/**
 * buildEcoAdvisor
 * ----------------
 * LAP-766 — Adapter qui transforme les VRAIES données carbone de l'utilisateur
 * en un objet exploitable par la page "Conseiller Écologie"
 * (`ConseillerEcologiePage`).
 *
 * Pattern identique à `buildTimeAdvisor` (Conseiller Temps) et
 * `buildAdvisorFromReports` (Conseiller Patrimonial) :
 *   - on agrège les sources Life (empreinte globale annuelle, recommandation
 *     Claude la plus récente, objectif de réduction) ;
 *   - on calcule un score global /100 + une décomposition par dimension
 *     (Logement / Transport / Alimentation / Shopping) ;
 *   - on produit un diagnostic (forces / faiblesses / risques) ;
 *   - on liste des actions prioritaires (avec route Life) ;
 *   - on prépare des prompts pour le chat conseiller.
 *
 * Toutes les chaînes affichées passent par `t` (i18next) pour rester
 * compatibles avec les 8 langues Life — comme les autres conseillers.
 *
 * Contrairement au temps, l'écologie « marche à l'envers » : une émission
 * BASSE est bonne. Le score d'une dimension mesure donc l'écart à un
 * référentiel (moyenne nationale × part de la catégorie). Émettre la moitié du
 * référentiel → 100 ; émettre le double → 0.
 */

// LAP-766 — Identity fallback when no t function is provided.
const identityT = (key, opts = {}) => {
  if (opts && typeof opts.defaultValue === 'string') return opts.defaultValue
  return key
}

// LAP-766 — Part de chaque catégorie dans l'empreinte « type » d'un Français
// moyen. Sert à dériver un référentiel par dimension à partir de la seule
// moyenne nationale renvoyée par le backend (NationalEmission.emission_moyen).
// Sources d'ordre de grandeur (ADEME / Carbone 4) : transport ~30 %,
// logement ~25 %, alimentation ~25 %, consommation/shopping ~20 %.
export const ECO_CATEGORY_SHARES = {
  logement: 0.25,
  transport: 0.3,
  alimentation: 0.25,
  shopping: 0.2,
}

// LAP-766 — Palette par dimension pour la décomposition (cohérente avec les
// cartes du dashboard écologique).
const CATEGORY_COLORS = {
  logement: '#7cc6b2',
  transport: '#7e9cdb',
  alimentation: '#f2b84b',
  shopping: '#a987ff',
}

// LAP-766 — Objectif « accord de Paris » : ~2 t CO2/an/personne. Sert de borne
// basse idéale pour contextualiser le score (affiché sur la jauge).
export const ECO_PARIS_TARGET = 2

function num(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function safeArr(value) {
  return Array.isArray(value) ? value : []
}

function clamp(value, min = 0, max = 100) {
  return Math.max(min, Math.min(max, value))
}

// LAP-766 — Même barème de statut que les autres conseillers.
function statusFromScore(score) {
  if (score >= 70) return 'good'
  if (score >= 45) return 'warning'
  return 'critical'
}

// LAP-766 — Score d'une dimension carbone par rapport à son référentiel.
//   ratio = émission / référentiel
//   ratio 0.5 → 100 (deux fois mieux que la moyenne)
//   ratio 1.0 → ~66 (dans la moyenne)
//   ratio 2.0 → 0   (deux fois pire que la moyenne)
// On borne entre 0 et 100. Si le référentiel est nul/inconnu, on renvoie null
// (dimension non évaluable).
function scoreFromBenchmark(value, benchmark) {
  if (!Number.isFinite(benchmark) || benchmark <= 0) return null
  const ratio = value / benchmark
  return clamp(Math.round(100 - (ratio - 0.5) * (200 / 3)))
}

/**
 * LAP-766 — Sélectionne l'entrée d'empreinte globale à analyser.
 *
 * `globalemission` renvoie un tableau de dicts par année, l'année courante en
 * tête. On prend cette première entrée (comportement du dashboard) ; en repli,
 * la première entrée non vide disponible.
 */
export function pickGlobalEmissionEntry(globalEmission, year = null) {
  const arr = safeArr(globalEmission)
  if (arr.length === 0) return null
  if (year != null) {
    const match = arr.find((e) => num(e?.date) === num(year))
    if (match) return match
  }
  return arr[0]
}

/**
 * buildEcoAdvisor — moteur de score écologique.
 *
 * @param {object} params
 * @param {Array}  params.globalEmission - tableau par année renvoyé par
 *   /api/Dashboard/reports/ (type "globalemission").
 * @param {object} params.advice - dernière recommandation Claude (DisplayedAdvice
 *   sérialisée) renvoyée par le backend (type "conseils"), ou {}.
 * @param {object} params.user - profil utilisateur (pour le prénom).
 * @param {Function} params.t - fonction i18next.
 * @returns {object} agrégat consommé par ConseillerEcologiePage.
 */
export function buildEcoAdvisor({
  globalEmission = [],
  advice = {},
  user = {},
  t = identityT,
} = {}) {
  const firstName =
    user?.first_name || user?.firstName || user?.prenom || ''

  const entry = pickGlobalEmissionEntry(globalEmission) || {}

  const emissionTotal = num(entry.emission_total)
  const emissionLogement = num(entry.emission_logement)
  const emissionTransport = num(entry.emission_transport)
  const emissionAlimentation = num(entry.emission_alimentation)
  const emissionShopping = num(entry.emission_shopping)
  // emission_moyen est une chaîne ("9.2") côté backend.
  const nationalAverage = num(entry.emission_moyen)
  const reductionPercentage = num(entry.pourcentage)
  const objectiveActive = Boolean(entry.objective_active)
  const objectiveValue = entry.objective_value ?? null

  // ── Dimensions ────────────────────────────────────────────────────────
  //
  // Référentiel par catégorie = moyenne nationale × part de la catégorie.
  // Une dimension est « évaluée » (available) uniquement si l'utilisateur a
  // saisi des données pour elle (émission > 0). Sinon elle part dans
  // `notEvaluated`, comme les conseillers temps/finance.
  const rawDimensions = [
    {
      key: 'logement',
      value: emissionLogement,
      label: t('conseillerEcologie.dimensions.logement', {
        defaultValue: 'Logement',
      }),
      weight: ECO_CATEGORY_SHARES.logement,
      route: '/domicile/monimmobilier',
      detail: t('conseillerEcologie.dimensions.logementDetail', {
        defaultValue:
          'Empreinte de votre logement (chauffage, énergie, surface) comparée à la moyenne nationale.',
      }),
      dataSource: t('conseillerEcologie.dimensions.logementData', {
        defaultValue: 'Données : vos biens immobiliers et leur empreinte annuelle.',
      }),
      emptyHint: t('conseillerEcologie.dimensions.logementEmpty', {
        defaultValue:
          'Aucun logement renseigné — ajoutez votre immobilier pour activer ce score.',
      }),
    },
    {
      key: 'transport',
      value: emissionTransport,
      label: t('conseillerEcologie.dimensions.transport', {
        defaultValue: 'Transport',
      }),
      weight: ECO_CATEGORY_SHARES.transport,
      route: '/domicile/mesvehicules',
      detail: t('conseillerEcologie.dimensions.transportDetail', {
        defaultValue:
          'Empreinte de vos déplacements (véhicules, carburant) comparée à la moyenne nationale.',
      }),
      dataSource: t('conseillerEcologie.dimensions.transportData', {
        defaultValue: 'Données : vos véhicules et vos dépenses de transport sur l’année.',
      }),
      emptyHint: t('conseillerEcologie.dimensions.transportEmpty', {
        defaultValue:
          'Aucun véhicule renseigné — ajoutez vos véhicules pour activer ce score.',
      }),
    },
    {
      key: 'alimentation',
      value: emissionAlimentation,
      label: t('conseillerEcologie.dimensions.alimentation', {
        defaultValue: 'Alimentation',
      }),
      weight: ECO_CATEGORY_SHARES.alimentation,
      route: '/Dashboard?questionnaire=alimentation',
      detail: t('conseillerEcologie.dimensions.alimentationDetail', {
        defaultValue:
          'Empreinte de votre alimentation (régime, produits) comparée à la moyenne nationale.',
      }),
      dataSource: t('conseillerEcologie.dimensions.alimentationData', {
        defaultValue: 'Données : votre questionnaire alimentation.',
      }),
      emptyHint: t('conseillerEcologie.dimensions.alimentationEmpty', {
        defaultValue:
          'Questionnaire alimentation non rempli — complétez-le pour activer ce score.',
      }),
    },
    {
      key: 'shopping',
      value: emissionShopping,
      label: t('conseillerEcologie.dimensions.shopping', {
        defaultValue: 'Consommation',
      }),
      weight: ECO_CATEGORY_SHARES.shopping,
      route: '/shopping',
      detail: t('conseillerEcologie.dimensions.shoppingDetail', {
        defaultValue:
          'Empreinte de votre consommation (achats, biens) comparée à la moyenne nationale.',
      }),
      dataSource: t('conseillerEcologie.dimensions.shoppingData', {
        defaultValue: 'Données : vos opérations financières classées « shopping ».',
      }),
      emptyHint: t('conseillerEcologie.dimensions.shoppingEmpty', {
        defaultValue:
          'Aucune dépense shopping détectée — vos opérations alimenteront ce score.',
      }),
    },
  ]

  const dimensions = rawDimensions.map((d) => {
    const benchmark = nationalAverage > 0 ? nationalAverage * d.weight : null
    const available = d.value > 0 && benchmark != null
    const score = available ? scoreFromBenchmark(d.value, benchmark) : null
    return {
      key: d.key,
      label: d.label,
      value: d.value,
      benchmark,
      score,
      weight: d.weight,
      available,
      route: d.route,
      detail: d.detail,
      dataSource: d.dataSource,
      emptyHint: d.emptyHint,
      status: available && score != null ? statusFromScore(score) : 'unknown',
    }
  })

  // ── Score global ──────────────────────────────────────────────────────
  // Moyenne pondérée sur les seules dimensions évaluées, avec re-normalisation
  // des poids (mirror buildTimeAdvisor). Aucune dimension évaluable → null.
  const evaluatedDims = dimensions.filter((d) => d.available && d.score != null)
  const evaluatedWeight = evaluatedDims.reduce((acc, d) => acc + d.weight, 0)
  const globalScore =
    evaluatedWeight > 0
      ? Math.round(
          evaluatedDims.reduce((acc, d) => acc + d.score * d.weight, 0) /
            evaluatedWeight,
        )
      : null

  // ── Diagnostic ────────────────────────────────────────────────────────
  const strengths = dimensions
    .filter((d) => d.available && d.status === 'good')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const weaknesses = dimensions
    .filter((d) => d.available && d.status === 'warning')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const risks = dimensions
    .filter((d) => d.available && d.status === 'critical')
    .map((d) => ({ key: d.key, title: d.label, detail: d.detail }))
  const notEvaluated = dimensions
    .filter((d) => !d.available)
    .map((d) => ({ key: d.key, title: d.label, detail: d.emptyHint }))

  // ── Décomposition (breakdown) ─────────────────────────────────────────
  const breakdownRaw = [
    { key: 'logement', value: emissionLogement },
    { key: 'transport', value: emissionTransport },
    { key: 'alimentation', value: emissionAlimentation },
    { key: 'shopping', value: emissionShopping },
  ]
  const breakdownTotal = breakdownRaw.reduce((acc, e) => acc + e.value, 0)
  const breakdownEntries = breakdownRaw
    .map((e) => {
      const dim = dimensions.find((d) => d.key === e.key)
      return {
        key: e.key,
        category: dim ? dim.label : e.key,
        value: Math.round(e.value * 100) / 100,
        percent:
          breakdownTotal > 0 ? Math.round((e.value / breakdownTotal) * 100) : 0,
        color: CATEGORY_COLORS[e.key] || '#7cc6b2',
      }
    })
    .sort((a, b) => b.value - a.value)

  // ── Actions prioritaires ──────────────────────────────────────────────
  // On cible les dimensions les plus éloignées du référentiel (critical d'abord,
  // puis warning), classées par score croissant pour traiter le pire en premier.
  const actionsPrioritaires = []
  const actionable = dimensions
    .filter((d) => d.available && d.score != null && d.status !== 'good')
    .sort((a, b) => a.score - b.score)
  actionable.forEach((d) => {
    actionsPrioritaires.push({
      id: `reduce-${d.key}`,
      title: t('conseillerEcologie.actions.reduceTitle', {
        defaultValue: `Réduire votre empreinte ${d.label.toLowerCase()}`,
        category: d.label,
      }),
      description: t('conseillerEcologie.actions.reduceDesc', {
        defaultValue: `${d.label} : ${Math.round(d.value * 100) / 100} t CO₂/an, au-dessus du référentiel (${
          Math.round(d.benchmark * 100) / 100
        } t). C'est votre meilleur levier de réduction.`,
        category: d.label,
        value: Math.round(d.value * 100) / 100,
        benchmark: Math.round(d.benchmark * 100) / 100,
      }),
      impact: d.status === 'critical' ? 'high' : 'medium',
      route: d.route,
    })
  })
  // Dimensions non évaluées → action « compléter les données ».
  notEvaluated.forEach((d) => {
    const dim = dimensions.find((x) => x.key === d.key)
    actionsPrioritaires.push({
      id: `complete-${d.key}`,
      title: t('conseillerEcologie.actions.completeTitle', {
        defaultValue: `Compléter vos données ${d.title.toLowerCase()}`,
        category: d.title,
      }),
      description: d.detail,
      impact: 'low',
      route: dim ? dim.route : '/Dashboard',
    })
  })
  // Pas d'objectif de réduction actif → en proposer un.
  if (!objectiveActive) {
    actionsPrioritaires.push({
      id: 'set-objective',
      title: t('conseillerEcologie.actions.objectiveTitle', {
        defaultValue: 'Fixer un objectif de réduction',
      }),
      description: t('conseillerEcologie.actions.objectiveDesc', {
        defaultValue:
          'Définissez un objectif annuel de réduction carbone pour suivre vos progrès dans le temps.',
      }),
      impact: 'medium',
      route: '/mesobjectifs',
    })
  }

  // ── Recommandation Claude (texte de la dernière reco persistée) ────────
  const recommendation = extractAdviceContent(advice)

  // ── Recommandations narratives (repli sans appel LLM) ─────────────────
  const recommendations = []
  if (globalScore != null) {
    if (emissionTotal > 0 && nationalAverage > 0) {
      const ratioPct = Math.round((emissionTotal / nationalAverage) * 100)
      if (ratioPct > 110) {
        recommendations.push(
          t('conseillerEcologie.reco.aboveAverage', {
            defaultValue: `Votre empreinte (${
              Math.round(emissionTotal * 100) / 100
            } t CO₂/an) est ~${ratioPct}% de la moyenne nationale (${nationalAverage} t). Concentrez-vous sur votre poste le plus élevé pour progresser vite.`,
            total: Math.round(emissionTotal * 100) / 100,
            ratio: ratioPct,
            national: nationalAverage,
          }),
        )
      } else if (ratioPct < 90) {
        recommendations.push(
          t('conseillerEcologie.reco.belowAverage', {
            defaultValue: `Votre empreinte (${
              Math.round(emissionTotal * 100) / 100
            } t CO₂/an) est sous la moyenne nationale (${nationalAverage} t) — beau travail. Visez maintenant l'objectif de 2 t/an des accords de Paris.`,
            total: Math.round(emissionTotal * 100) / 100,
            national: nationalAverage,
          }),
        )
      }
    }
  }
  if (breakdownEntries.length && breakdownEntries[0].value > 0) {
    const top = breakdownEntries[0]
    recommendations.push(
      t('conseillerEcologie.reco.topCategory', {
        defaultValue: `Votre principal poste d'émission est « ${top.category} » (${top.percent}% du total). Tout effort sur ce poste a l'impact le plus fort.`,
        category: top.category,
        percent: top.percent,
      }),
    )
  }
  if (objectiveActive && reductionPercentage > 0) {
    recommendations.push(
      t('conseillerEcologie.reco.objectiveActive', {
        defaultValue: `Objectif de réduction actif : -${reductionPercentage}%. Gardez le cap en agissant en priorité sur vos postes critiques.`,
        pct: reductionPercentage,
      }),
    )
  }

  // ── Prompts pour le chat conseiller ────────────────────────────────────
  const suggestedPrompts = [
    t('conseillerEcologie.prompts.reduceFootprint', {
      defaultValue: 'Comment réduire mon empreinte carbone cette année ?',
    }),
    t('conseillerEcologie.prompts.topLever', {
      defaultValue: 'Quel est mon meilleur levier de réduction ?',
    }),
    t('conseillerEcologie.prompts.compare', {
      defaultValue: 'Comment je me situe par rapport à la moyenne ?',
    }),
    t('conseillerEcologie.prompts.parisTarget', {
      defaultValue: 'Comment atteindre l’objectif de 2 tonnes par an ?',
    }),
  ]

  return {
    userProfile: {
      firstName,
    },
    totals: {
      emissionTotal,
      nationalAverage,
      parisTarget: ECO_PARIS_TARGET,
      reductionPercentage,
      objectiveActive,
      objectiveValue,
      year: num(entry.date) || null,
    },
    metrics: {
      emissionTotal,
      emissionLogement,
      emissionTransport,
      emissionAlimentation,
      emissionShopping,
      nationalAverage,
    },
    ecoScore: {
      global: globalScore,
      dimensions,
      evaluatedCount: evaluatedDims.length,
      totalCount: dimensions.length,
    },
    diagnostic: {
      strengths,
      weaknesses,
      risks,
      notEvaluated,
    },
    breakdown: {
      entries: breakdownEntries,
      total: Math.round(breakdownTotal * 100) / 100,
    },
    actionsPrioritaires,
    recommendation,
    recommendations,
    suggestedPrompts,
  }
}

/**
 * LAP-766 — Extrait le texte de recommandation d'une ligne DisplayedAdvice.
 * `content` peut être du texte brut, ou `data` peut contenir un JSON structuré
 * (cf. ConseilsCard.parseAdvicePayload). On reste tolérant.
 */
export function extractAdviceContent(advice) {
  if (!advice || typeof advice !== 'object') return ''
  if (typeof advice.content === 'string' && advice.content.trim()) {
    return advice.content.trim()
  }
  // Repli : essayer de lire un champ texte dans data (JSON sérialisé).
  if (typeof advice.data === 'string' && advice.data.trim()) {
    try {
      const parsed = JSON.parse(advice.data)
      if (typeof parsed === 'string') return parsed
      if (parsed && typeof parsed.summary === 'string') return parsed.summary
      if (parsed && typeof parsed.content === 'string') return parsed.content
    } catch (_) {
      return advice.data.trim()
    }
  }
  return ''
}
